import React, { createContext, useContext, useState, useCallback, useEffect, useRef } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { normalizeTranslationValue, sanitizeTranslationCache } from '@/lib/translation/translationCache';

export type Language = 'en' | 'es' | 'fr' | 'pt' | 'it' | 'de' | 'he' | 'ar';

export const LANGUAGES: { code: Language; name: string; flag: string; dir?: 'rtl' }[] = [
  { code: 'en', name: 'English', flag: '🇺🇸' },
  { code: 'es', name: 'Español', flag: '🇪🇸' },
  { code: 'fr', name: 'Français', flag: '🇫🇷' },
  { code: 'pt', name: 'Português', flag: '🇧🇷' },
  { code: 'it', name: 'Italiano', flag: '🇮🇹' },
  { code: 'de', name: 'Deutsch', flag: '🇩🇪' },
  { code: 'he', name: 'עברית', flag: '🇮🇱', dir: 'rtl' },
  { code: 'ar', name: 'العربية', flag: '🇸🇦', dir: 'rtl' },
];

interface TranslationCache {
  [key: string]: {
    [lang: string]: string;
  };
}

interface PendingTranslation {
  text: string;
  cacheKey: string;
  resolve: (value: string) => void;
}

interface TranslationContextType {
  currentLanguage: Language;
  setLanguage: (lang: Language) => void;
  translate: (text: string, key?: string) => Promise<string>;
  translateBatch: (texts: string[]) => Promise<string[]>;
  isTranslating: boolean;
  isRTL: boolean;
}

const TranslationContext = createContext<TranslationContextType | undefined>(undefined);

const STORAGE_KEY = 'preferred_language';
const CACHE_KEY = 'translation_cache';
const BATCH_DELAY = 100; // ms to wait before sending batch
const MAX_BATCH_SIZE = 50; // Maximum texts per batch

export function TranslationProvider({ children }: { children: React.ReactNode }) {
  const [currentLanguage, setCurrentLanguage] = useState<Language>(() => {
    const stored = localStorage.getItem(STORAGE_KEY);
    return (stored as Language) || 'en';
  });
  const [cache, setCache] = useState<TranslationCache>(() => {
    try {
      const stored = localStorage.getItem(CACHE_KEY);
      if (!stored) return {};
      return sanitizeTranslationCache(JSON.parse(stored));
    } catch {
      return {};
    }
  });
  const [isTranslating, setIsTranslating] = useState(false);

  // Batching state
  const pendingQueue = useRef<PendingTranslation[]>([]);
  const batchTimeout = useRef<ReturnType<typeof setTimeout> | null>(null);
  const processingBatch = useRef(false);

  const isRTL = currentLanguage === 'he' || currentLanguage === 'ar';

  // Update document direction for RTL languages
  useEffect(() => {
    document.documentElement.dir = isRTL ? 'rtl' : 'ltr';
    document.documentElement.lang = currentLanguage;
  }, [currentLanguage, isRTL]);

  // Save cache to localStorage when it changes
  useEffect(() => {
    try {
      localStorage.setItem(CACHE_KEY, JSON.stringify(cache));
    } catch {
      // localStorage might be full, ignore
    }
  }, [cache]);

  const setLanguage = useCallback((lang: Language) => {
    setCurrentLanguage(lang);
    localStorage.setItem(STORAGE_KEY, lang);
  }, []);

  // Process the pending queue
  const processBatch = useCallback(async () => {
    if (processingBatch.current || pendingQueue.current.length === 0) return;
    
    processingBatch.current = true;
    setIsTranslating(true);

    // Take up to MAX_BATCH_SIZE items
    const batch = pendingQueue.current.splice(0, MAX_BATCH_SIZE);
    
    // Get unique texts to translate (avoid duplicates in same batch)
    const uniqueTexts = [...new Set(batch.map(p => p.text))];
    const textToResult: Record<string, string> = {};

    try {
      const { data, error } = await supabase.functions.invoke('translate', {
        body: { 
          texts: uniqueTexts,
          targetLanguage: currentLanguage
        }
      });

      if (error) throw error;

      const translations = data?.translations || [];
      
      // Map results back
      uniqueTexts.forEach((text, idx) => {
        const translatedRaw = translations[idx];
        textToResult[text] = normalizeTranslationValue(translatedRaw, text);
      });

      // Update cache with all results
      setCache(prev => {
        const newCache = { ...prev };
        batch.forEach(p => {
          const translated = textToResult[p.text] || p.text;
          newCache[p.cacheKey] = {
            ...newCache[p.cacheKey],
            [currentLanguage]: translated
          };
        });
        return newCache;
      });

      // Resolve all promises
      batch.forEach(p => {
        p.resolve(textToResult[p.text] || p.text);
      });

    } catch (error) {
      console.error('Batch translation error:', error);
      // Resolve with original texts on error
      batch.forEach(p => p.resolve(p.text));
    } finally {
      processingBatch.current = false;
      setIsTranslating(false);
      
      // Process next batch if there are more items
      if (pendingQueue.current.length > 0) {
        setTimeout(processBatch, BATCH_DELAY);
      }
    }
  }, [currentLanguage]);

  const translate = useCallback(async (text: string, key?: string): Promise<string> => {
    // If English, return original
    if (currentLanguage === 'en') return text;

    const cacheKey = key || text;
    
    // Check cache first
    const cached = cache[cacheKey]?.[currentLanguage];
    if (cached !== undefined) {
      return normalizeTranslationValue(cached, text);
    }

    // Add to pending queue and return promise
    return new Promise<string>((resolve) => {
      pendingQueue.current.push({ text, cacheKey, resolve });
      
      // Clear existing timeout and set new one
      if (batchTimeout.current) {
        clearTimeout(batchTimeout.current);
      }
      
      batchTimeout.current = setTimeout(() => {
        processBatch();
      }, BATCH_DELAY);
    });
  }, [currentLanguage, cache, processBatch]);

  const translateBatch = useCallback(async (texts: string[]): Promise<string[]> => {
    // If English, return originals
    if (currentLanguage === 'en') return texts;

    // Check which texts need translation
    const results: string[] = [...texts];
    const textsToTranslate: { index: number; text: string }[] = [];

    texts.forEach((text, index) => {
      const cached = cache[text]?.[currentLanguage];
      if (cached !== undefined) {
        results[index] = normalizeTranslationValue(cached, text);
      } else {
        textsToTranslate.push({ index, text });
      }
    });

    if (textsToTranslate.length === 0) {
      return results;
    }

    // Create promises for each text that needs translation
    const promises = textsToTranslate.map(item => 
      translate(item.text).then(translated => {
        results[item.index] = translated;
      })
    );

    await Promise.all(promises);
    return results;
  }, [currentLanguage, cache, translate]);

  return (
    <TranslationContext.Provider value={{
      currentLanguage,
      setLanguage,
      translate,
      translateBatch,
      isTranslating,
      isRTL
    }}>
      {children}
    </TranslationContext.Provider>
  );
}

export function useTranslation() {
  const context = useContext(TranslationContext);
  if (!context) {
    throw new Error('useTranslation must be used within a TranslationProvider');
  }
  return context;
}